import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import nintendoSearchService from "../service/nintendoSearch.service";
import { reachGoal } from "../utils/metrika";
import { matchSiteLinks, normalizeQuery } from "./siteLinks";

const GAMES_LIMIT = 5;
const rubFormat = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });

function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function buildItems(text, games) {
  const query = text.trim();
  const pages = matchSiteLinks(query).map((link) => ({
    key: link.id,
    type: link.storeId ? "store" : "page",
    label: link.label,
    hint: link.hint,
    to: link.to,
    href: link.href,
  }));
  const gameItems = (games || []).map((game) => ({
    key: `game-${game.nsuid}`,
    type: "game",
    label: game.title,
    hint: game.rub ? `от ${rubFormat.format(Math.round(game.rub))} ₽ · Nintendo Switch` : "Nintendo Switch",
    percent: game.percent,
    image: game.image,
    href: game.seoPath,
  }));
  const encoded = encodeURIComponent(query);
  const more =
    normalizeQuery(query).length >= 2
      ? [
          { key: "more-steam", type: "more", label: `Искать «${query}» в скидках Steam`, to: `/steam-discount?q=${encoded}` },
          { key: "more-nintendo", type: "more", label: `Искать «${query}» в скидках Nintendo`, to: `/nintendo-discount?q=${encoded}` },
        ]
      : [];
  return [...pages, ...gameItems, ...more];
}

/** Поиск по сайту: разделы и магазины — сразу, игры Nintendo — с сервера. */
function SiteSearch({ onNavigate, autoFocus = false, inputId = "site-search" }) {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [games, setGames] = useState(null);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const query = useDebounced(text.trim(), 300);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (normalizeQuery(query).length < 2) {
      setGames(null);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    nintendoSearchService
      .search("us", query, controller.signal)
      .then((data) => {
        setGames((data.results || []).filter((game) => game.seoPath).slice(0, GAMES_LIMIT));
      })
      .catch((error) => {
        if (error.name !== "AbortError") setGames([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [query]);

  const items = useMemo(() => buildItems(text, query === text.trim() ? games : null), [text, query, games]);

  useEffect(() => {
    setActive(0);
  }, [text]);

  const chosen = (item) => {
    reachGoal("site_search", { type: item.type });
    onNavigate?.();
  };

  const open = (item) => {
    chosen(item);
    if (item.to) {
      navigate(item.to);
    } else if (item.href) {
      window.location.assign(item.href);
    }
  };

  const onKeyDown = (event) => {
    if (!items.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((index) => (index + 1) % items.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => (index - 1 + items.length) % items.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      open(items[Math.min(active, items.length - 1)]);
    }
  };

  const typed = normalizeQuery(text).length >= 2;
  const listId = `${inputId}-results`;

  return (
    <div className="site-search">
      <div className="site-search-field">
        <MagnifyingGlassIcon className="site-search-icon" aria-hidden="true" />
        <input
          ref={inputRef}
          id={inputId}
          type="search"
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Игра, магазин или раздел: zelda, roblox, скидки…"
          aria-label="Поиск по сайту"
          role="combobox"
          aria-expanded={typed}
          aria-controls={listId}
          aria-activedescendant={typed && items[active] ? `${listId}-${items[active].key}` : undefined}
          autoComplete="off"
        />
      </div>
      {typed ? (
        <ul id={listId} className="site-search-results" role="listbox" aria-label="Результаты поиска">
          {items.map((item, index) => {
            const content = (
              <>
                {item.type === "game" ? (
                  item.image ? (
                    <img src={item.image} alt="" className="site-search-thumb" loading="lazy" />
                  ) : (
                    <span className="site-search-thumb" aria-hidden="true" />
                  )
                ) : null}
                <span className="min-w-0 flex-1">
                  <span className="site-search-label">{item.label}</span>
                  {item.hint ? <span className="site-search-hint">{item.hint}</span> : null}
                </span>
                {item.percent ? <span className="site-search-sale">−{item.percent}%</span> : null}
              </>
            );
            const props = {
              className: `site-search-item site-search-item-${item.type}${index === active ? " is-active" : ""}`,
              onMouseEnter: () => setActive(index),
              onClick: () => chosen(item),
            };
            return (
              <li key={item.key} id={`${listId}-${item.key}`} role="option" aria-selected={index === active}>
                {item.to ? (
                  <Link to={item.to} {...props}>
                    {content}
                  </Link>
                ) : (
                  <a href={item.href} {...props}>
                    {content}
                  </a>
                )}
              </li>
            );
          })}
          {loading ? <li className="site-search-status">Ищем игры…</li> : null}
        </ul>
      ) : null}
    </div>
  );
}

export default SiteSearch;
